import React, { useMemo, useState } from "react";
import { Q } from "../constants/testIds";
import { TOOL_SUGGESTIONS } from "../lib/questions";

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const QuestionScreen = ({
  question,
  stepIndex,
  totalSteps,
  value,
  otherValue,
  toolsOtherList,
  onChange,
  onOtherChange,
  onToolsOtherListChange,
  onBack,
  onContinue,
  canGoBack,
  canContinue,
  continueLabel = "Continue",
}) => {
  const isMulti = question.type === "multi";
  const showOther =
    question.otherField &&
    (isMulti
      ? Array.isArray(value) && value.includes("Other")
      : value === "Other");

  const progressText = `Question ${stepIndex + 1} of ${totalSteps}`;
  const progressPct = ((stepIndex + 1) / totalSteps) * 100;

  const [search, setSearch] = useState("");
  const additionalTools = useMemo(
    () => toolsOtherList || [],
    [toolsOtherList]
  );
  const lowerSearch = search.trim().toLowerCase();
  const filteredSuggestions = useMemo(() => {
    if (!question.searchable || !lowerSearch) return [];
    return TOOL_SUGGESTIONS.filter(
      (t) =>
        t.toLowerCase().includes(lowerSearch) &&
        !additionalTools.includes(t)
    ).slice(0, 8);
  }, [lowerSearch, additionalTools, question.searchable]);

  const handleOptionClick = (opt) => {
    if (!isMulti) {
      onChange(opt);
      return;
    }
    const current = Array.isArray(value) ? value : [];
    const exclusive = question.exclusive || [];
    let next;

    if (current.includes(opt)) {
      next = current.filter((v) => v !== opt);
    } else if (exclusive.includes(opt)) {
      // Picking an exclusive option clears everything else (but keep "Other" for Q7/Q8)
      const keepOther =
        question.id === "duplicated_workflows" ||
        question.id === "problem_areas";
      next = keepOther && current.includes("Other") ? [opt, "Other"] : [opt];
    } else {
      // Picking a non-exclusive option deselects all exclusive ones
      next = current.filter((v) => !exclusive.includes(v));
      next.push(opt);
    }
    onChange(next);
  };

  const isSelected = (opt) => {
    if (isMulti) return Array.isArray(value) && value.includes(opt);
    return value === opt;
  };

  const addTool = (tool) => {
    if (!tool) return;
    if (additionalTools.includes(tool)) return;
    onToolsOtherListChange([...additionalTools, tool]);
    setSearch("");
  };

  const removeTool = (tool) => {
    onToolsOtherListChange(additionalTools.filter((t) => t !== tool));
  };

  return (
    <div
      className="gw-card gw-fade-up"
      data-testid={Q.questionScreen}
      data-question-id={question.id}
    >
      {/* Sticky header */}
      <div className="gw-card-header flex flex-col gap-3" style={{ marginBottom: 20 }}>
        <div className="flex items-center justify-between">
          <span className="gw-small" data-testid={Q.questionProgressText}>
            {progressText}
          </span>
          <span className="gw-caption">{Math.round(progressPct)}%</span>
        </div>
        <div className="gw-progress-bar" data-testid={Q.progressBar}>
          <div
            className="gw-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Scrollable body */}
      <div className="gw-card-body flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h2 className="gw-question" data-testid={Q.questionTitle}>
            {question.question}
          </h2>
          {isMulti && (
            <span className="gw-helper">Select all that apply.</span>
          )}
        </div>

        <div className="flex flex-col gap-2.5">
          {question.options.map((opt) => {
            const selected = isSelected(opt);
            return (
              <button
                key={opt}
                type="button"
                className="gw-option"
                data-selected={selected}
                onClick={() => handleOptionClick(opt)}
                data-testid={`${Q.optionPrefix}${question.id}-${slug(opt)}`}
              >
                <span
                  className={`gw-option-marker ${isMulti ? "square" : ""}`}
                  aria-hidden="true"
                />
                <span className="flex-1">{opt}</span>
              </button>
            );
          })}
        </div>

        {showOther && (
          <input
            type="text"
            className="gw-input"
            placeholder="Please specify"
            value={otherValue || ""}
            onChange={(e) => onOtherChange(e.target.value)}
            data-testid={Q.otherInput}
          />
        )}

        {question.searchable && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label
                className="gw-small"
                style={{ color: "var(--text-secondary)" }}
              >
                Add another tool
              </label>
              <input
                type="text"
                className="gw-input"
                placeholder="Search tools (e.g. Airtable, Odoo)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && search.trim()) {
                    e.preventDefault();
                    addTool(search.trim());
                  }
                }}
                data-testid={Q.searchInput}
              />
              {lowerSearch && (
                <div className="gw-suggestion-list">
                  {filteredSuggestions.length === 0 ? (
                    <button
                      type="button"
                      className="gw-suggestion-item"
                      onClick={() => addTool(search.trim())}
                      data-testid={`${Q.suggestionItemPrefix}custom`}
                    >
                      Add “{search.trim()}”
                    </button>
                  ) : (
                    filteredSuggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="gw-suggestion-item"
                        onClick={() => addTool(s)}
                        data-testid={`${Q.suggestionItemPrefix}${slug(s)}`}
                      >
                        {s}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {additionalTools.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {additionalTools.map((tool) => (
                  <span
                    key={tool}
                    className="gw-chip"
                    data-testid={`${Q.chipPrefix}${slug(tool)}`}
                  >
                    {tool}
                    <button
                      type="button"
                      className="gw-chip-remove"
                      onClick={() => removeTool(tool)}
                      aria-label={`Remove ${tool}`}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M3 3L9 9M9 3L3 9"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky actions */}
      <div className="gw-card-actions flex flex-col gap-4" style={{ marginTop: 20 }}>
        <hr className="gw-divider" />
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            className="gw-btn-secondary"
            style={{ flex: 1 }}
            onClick={onBack}
            disabled={!canGoBack}
            data-testid={Q.backBtn}
          >
            Back
          </button>
          <button
            type="button"
            className="gw-btn-primary"
            style={{ flex: 1 }}
            onClick={onContinue}
            disabled={!canContinue}
            data-testid={Q.continueBtn}
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;
